import useCachedValueIfNotNull from '@/utils/CachedValueIf';
import useDebounce from '@/utils/useDebounce';
import { useEffect, useMemo, useRef, useState } from 'react';
import type { Device, DeviceQueryParams } from '../types';
import { useDevices, useDevicesCount } from './useDevices';

// The part of the list state a host may want to keep somewhere durable, e.g. URL search params.
export interface DeviceListState {
  page: number;
  pageSize: number;
  search: string;
}

// Server-side ordering. The table has no sortable headers, so only a host can set it.
export interface DeviceListSort {
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

interface UseDeviceListOptions {
  companyId: string;
  // Seeds paging and search once on mount.
  initialState?: Partial<DeviceListState>;
  // Reports paging and the settled search term on every change, so a host can persist them.
  onStateChange?: (state: DeviceListState) => void;
  sort?: DeviceListSort;
}

const DEFAULT_STATE: DeviceListState = { page: 1, pageSize: 10, search: '' };

// Holds the paging, search and selection state for a device list scoped to one company.
// A host that has nowhere to store the state just omits initialState/onStateChange and
// the hook keeps everything local, so several lists can live next to each other.
export function useDeviceList({ companyId, initialState, onStateChange, sort }: UseDeviceListOptions) {
  // Spreading would let an absent search param overwrite a default with undefined.
  const seed: DeviceListState = {
    page: initialState?.page ?? DEFAULT_STATE.page,
    pageSize: initialState?.pageSize ?? DEFAULT_STATE.pageSize,
    search: initialState?.search ?? DEFAULT_STATE.search,
  };
  const [searchInput, setSearchInput] = useState(seed.search.toString());
  const [page, setPage] = useState(seed.page);
  const [pageSize, setPageSize] = useState(seed.pageSize);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  const debouncedSearch = useDebounce(searchInput);
  const { sortBy, sortOrder } = sort ?? {};

  const queryParams: DeviceQueryParams = useMemo(
    () => ({
      page,
      pageSize,
      search: debouncedSearch || undefined,
      sortBy,
      sortOrder,
    }),
    [page, pageSize, debouncedSearch, sortBy, sortOrder]
  );

  const countParams = useMemo(() => ({ search: debouncedSearch || undefined }), [debouncedSearch]);

  const { data: devicesResult, isLoading, isFetching } = useDevices(companyId, queryParams);
  const { data: countResult } = useDevicesCount(companyId, countParams, pageSize);

  const serverDevices = useCachedValueIfNotNull(devicesResult, isLoading, {
    data: [] as Device[],
    page: 1,
    pageSize: seed.pageSize,
  });

  // The server already filters by the debounced term; this keeps the table in sync
  // while the user is still typing.
  const devices = useMemo(() => {
    const searchLower = searchInput.toLowerCase();
    return serverDevices.data.filter(
      (device) =>
        device.serialNumber.toLowerCase().includes(searchLower) ||
        device.modelTypeId.toLowerCase().includes(searchLower) ||
        device.company?.name?.toLowerCase().includes(searchLower)
    );
  }, [serverDevices, searchInput]);

  // The seed already describes the mounted state, so only later changes are worth reporting.
  const hasMounted = useRef(false);
  useEffect(() => {
    if (!hasMounted.current) {
      hasMounted.current = true;
      return;
    }
    onStateChange?.({ page, pageSize, search: debouncedSearch });
  }, [page, pageSize, debouncedSearch, onStateChange]);

  // A different search term means a different result set, so the paging starts over.
  const changeSearch = (value: string) => {
    setSearchInput(value);
    setPage(1);
  };

  const clearSelection = () => setSelectedIds(new Set());

  const changePage = (newPage: number) => {
    setPage(newPage);
    clearSelection();
  };

  const changePageSize = (newPageSize: number) => {
    setPageSize(newPageSize);
    setPage(1);
    clearSelection();
  };

  return {
    searchInput,
    changeSearch,
    page,
    changePage,
    pageSize,
    changePageSize,
    selectedIds,
    setSelectedIds,
    clearSelection,
    devices,
    total: countResult?.total ?? 0,
    isLoading,
    isFetching,
  };
}
